import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Navigation, Package, Users, Truck, MapPin, DollarSign, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DispatchPage = () => {
  const [loads, setLoads] = useState([]);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);

  useEffect(() => {
    fetchLoads();
  }, []);

  const fetchLoads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/loads?status=pending`);
      setLoads(response.data);
    } catch (error) {
      console.error('Error fetching loads:', error);
      toast.error('Failed to fetch loads');
    } finally {
      setLoading(false);
    }
  };

  const findMatches = async (loadId) => {
    try {
      setMatching(true);
      const response = await axios.get(`${API}/loads/${loadId}/matches?max_distance=300`);
      setMatches(response.data);
      setSelectedLoad(loads.find(l => l.id === loadId));
      if (response.data.length === 0) {
        toast.info('No matches found within 300 miles');
      } else {
        toast.success(`Found ${response.data.length} potential matches`);
      }
    } catch (error) {
      console.error('Error finding matches:', error);
      toast.error('Failed to find matches');
    } finally {
      setMatching(false);
    }
  };

  const assignLoad = async (loadId, driverId, truckId) => {
    try {
      await axios.post(`${API}/loads/${loadId}/assign?driver_id=${driverId}&truck_id=${truckId}`);
      toast.success('Load assigned successfully');
      fetchLoads();
      setSelectedLoad(null);
      setMatches([]);
    } catch (error) {
      console.error('Error assigning load:', error);
      toast.error('Failed to assign load');
    }
  };

  const LoadCard = ({ load, onFindMatch }) => (
    <Card 
      className="glass-card hover:border-primary/30 transition-all duration-300 cursor-pointer"
      onClick={() => onFindMatch(load.id)}
      data-testid={`dispatch-load-card-${load.id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/15">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-mono text-muted-foreground">LOAD #{load.id.substring(0, 8)}</p>
              <p className="text-sm font-semibold text-foreground mt-1">{load.commodity}</p>
            </div>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-primary mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Pickup</p>
              <p className="text-sm text-foreground">{load.pickup_address.substring(0, 50)}...</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-emerald-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Delivery</p>
              <p className="text-sm text-foreground">{load.delivery_address.substring(0, 50)}...</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs">
          <div>
            <span className="text-muted-foreground">Weight:</span>
            <span className="font-mono font-semibold text-foreground ml-1">{load.weight} lbs</span>
          </div>
          <div>
            <span className="text-muted-foreground">Rate:</span>
            <span className="font-mono font-semibold text-foreground ml-1">${load.rate}</span>
          </div>
        </div>

        <Button 
          onClick={(e) => {
            e.stopPropagation();
            onFindMatch(load.id);
          }}
          className="w-full mt-4 glow-button"
          data-testid={`find-matches-${load.id}`}
        >
          <Navigation className="w-4 h-4 mr-2" />
          Find Matches
        </Button>
      </CardContent>
    </Card>
  );

  const MatchCard = ({ match, onAssign }) => (
    <Card className="glass-card border-emerald-500/30" data-testid={`match-card-${match.driver_id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/15">
              <Users className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{match.driver_name}</p>
              <p className="text-xs font-mono text-muted-foreground">Truck: {match.truck_number}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Match Score</p>
            <p className="text-2xl font-black font-heading text-emerald-500">{match.score}</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between p-2 rounded bg-background/50">
            <span className="text-xs text-muted-foreground">Distance to Pickup</span>
            <span className="text-sm font-mono font-semibold text-foreground">{match.distance_to_pickup} mi</span>
          </div>
        </div>

        <Button 
          onClick={() => onAssign(match.driver_id, match.truck_id)}
          className="w-full glow-button bg-emerald-500 hover:bg-emerald-600"
          data-testid={`assign-match-${match.driver_id}`}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Assign Load
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="dispatch-page-container">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Dispatch Operations</h1>
            <p className="text-muted-foreground mt-2">Match loads with available drivers and trucks</p>
          </div>
          <Button onClick={fetchLoads} className="glow-button" data-testid="refresh-loads-button">
            Refresh Loads
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-xl font-heading flex items-center gap-2">
                  <Package className="w-5 h-5 text-primary" />
                  Pending Loads ({loads.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-sm text-muted-foreground">Loading...</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                    {loads.map((load) => (
                      <LoadCard key={load.id} load={load} onFindMatch={findMatches} />
                    ))}
                    {loads.length === 0 && (
                      <div className="text-center py-12">
                        <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                        <p className="text-sm text-muted-foreground">No pending loads</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2">
            {selectedLoad ? (
              <div className="space-y-6">
                <Card className="glass-card bg-primary/5 border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-xl font-heading">Selected Load Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Commodity</p>
                        <p className="text-sm font-semibold text-foreground">{selectedLoad.commodity}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Weight</p>
                        <p className="text-sm font-mono font-semibold text-foreground">{selectedLoad.weight} lbs</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Distance</p>
                        <p className="text-sm font-mono font-semibold text-foreground">{selectedLoad.distance_miles} mi</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Rate</p>
                        <p className="text-sm font-mono font-semibold text-foreground">${selectedLoad.rate}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Pickup Location</p>
                      <p className="text-sm text-foreground">{selectedLoad.pickup_address}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Delivery Location</p>
                      <p className="text-sm text-foreground">{selectedLoad.delivery_address}</p>
                    </div>
                    <Button 
                      onClick={() => {
                        setSelectedLoad(null);
                        setMatches([]);
                      }}
                      variant="outline"
                      className="w-full"
                      data-testid="clear-selection-button"
                    >
                      Clear Selection
                    </Button>
                  </CardContent>
                </Card>

                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-heading flex items-center gap-2">
                      <Navigation className="w-5 h-5 text-emerald-500" />
                      Best Matches ({matches.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {matching ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                          <p className="text-sm text-muted-foreground">Finding best matches...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                        {matches.map((match) => (
                          <MatchCard 
                            key={match.driver_id} 
                            match={match} 
                            onAssign={(driverId, truckId) => assignLoad(selectedLoad.id, driverId, truckId)}
                          />
                        ))}
                        {matches.length === 0 && !matching && (
                          <div className="text-center py-12">
                            <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                            <p className="text-sm text-muted-foreground">No matches found</p>
                            <p className="text-xs text-muted-foreground mt-2">Try increasing the search radius or add more drivers</p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="glass-card h-full min-h-[600px]" data-testid="dispatch-instructions">
                <CardContent className="p-12 flex items-center justify-center h-full">
                  <div className="text-center max-w-md">
                    <div className="flex justify-center gap-4 mb-6">
                      <div className="p-4 rounded-lg bg-primary/15">
                        <Package className="w-8 h-8 text-primary" />
                      </div>
                      <div className="p-4 rounded-lg bg-emerald-500/15">
                        <Navigation className="w-8 h-8 text-emerald-500" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-black font-heading text-foreground mb-3">Intelligent Load Matching</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Select a load from the left panel to find the best driver-truck combinations based on:
                    </p>
                    <div className="space-y-2 text-left">
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-background/50">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <p className="text-sm text-foreground">Distance to pickup location</p>
                      </div>
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-background/50">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <p className="text-sm text-foreground">Truck capacity vs load weight</p>
                      </div>
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-background/50">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <p className="text-sm text-foreground">Driver and truck availability</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DispatchPage;
